<template>
  <b-modal
    id="modal-visualizar-saldo"
    :title="null"
    size="lg"
    hide-footer
    centered
    hide-header
    body-class="p-0"
    @hidden="$emit('hidden')"
  >
    <div v-if="saldo" class="saldo-view">
      <!-- Header -->
      <div class="saldo-view__header">
        <div class="saldo-view__header-info">
          <div class="saldo-view__title">
            <feather-icon icon="RotateCcwIcon" size="18" class="mr-50" />
            {{ saldo.nome_abrev || saldo.cliente_nome || 'Cliente' }}
          </div>
          <div class="saldo-view__subtitle">
            Emitente <strong>{{ saldo.cod_emitente }}</strong>
            <span class="mx-50">•</span>
            Contrato <strong>{{ saldo.nr_contrato || '—' }}</strong>
            <span class="mx-50">•</span>
            Seq. <strong>{{ saldo.sequencia }}</strong>
          </div>
        </div>
        <div class="saldo-view__badges">
          <b-badge :variant="saldo.ativo ? 'success' : 'secondary'" class="mr-50">
            {{ saldo.ativo ? 'Ativo' : 'Inativo' }}
          </b-badge>
          <b-badge :variant="saldo.considera ? 'info' : 'secondary'">
            {{ saldo.considera ? 'Considera' : 'Não considera' }}
          </b-badge>
        </div>
        <b-button
          variant="flat-light"
          size="sm"
          class="saldo-view__close"
          @click="$bvModal.hide('modal-visualizar-saldo')"
        >
          <feather-icon icon="XIcon" size="18" />
        </b-button>
      </div>

      <div class="saldo-view__body">
        <!-- Destaque: Saldo Final -->
        <div class="saldo-view__highlight">
          <div class="saldo-view__highlight-item">
            <span class="label">Saldo Final</span>
            <span class="value value--primary">{{ formatMoney(saldo.vl_saldo_final) }}</span>
          </div>
          <div class="saldo-view__highlight-item">
            <span class="label">Saldo Confirmado</span>
            <span class="value">{{ formatMoney(saldo.vl_saldo_confirmado) }}</span>
          </div>
          <div class="saldo-view__highlight-item">
            <span class="label">% Devolução</span>
            <span class="value">{{ formatPerc(saldo.perc_devol) }}</span>
          </div>
        </div>

        <!-- Identificação -->
        <h6 class="saldo-view__section-title">
          <feather-icon icon="FileTextIcon" size="14" class="mr-50" /> Identificação
        </h6>
        <b-row class="saldo-view__grid">
          <b-col cols="6" md="3"><div class="field"><span class="field-label">Tipo Devol.</span><span class="field-value">{{ formatTipoDevol(saldo.tipo_devol) }}</span></div></b-col>
          <b-col cols="6" md="3"><div class="field"><span class="field-label">Cód. Lista</span><span class="field-value">{{ saldo.cod_lista || '—' }}</span></div></b-col>
          <b-col cols="6" md="3"><div class="field"><span class="field-label">Razão Social</span><span class="field-value">{{ saldo.cliente_nome || '—' }}</span></div></b-col>
          <b-col cols="6" md="3"><div class="field"><span class="field-label">Sequência</span><span class="field-value">{{ saldo.sequencia }}</span></div></b-col>
        </b-row>

        <!-- Períodos -->
        <h6 class="saldo-view__section-title">
          <feather-icon icon="CalendarIcon" size="14" class="mr-50" /> Períodos
        </h6>
        <b-row class="saldo-view__grid">
          <b-col cols="6" md="3"><div class="field"><span class="field-label">Fat. Início</span><span class="field-value">{{ formatDate(saldo.periodo_fat_ini) }}</span></div></b-col>
          <b-col cols="6" md="3"><div class="field"><span class="field-label">Fat. Fim</span><span class="field-value">{{ formatDate(saldo.periodo_fat_fim) }}</span></div></b-col>
          <b-col cols="6" md="3"><div class="field"><span class="field-label">Devol. Início</span><span class="field-value">{{ formatDate(saldo.periodo_devol_ini) }}</span></div></b-col>
          <b-col cols="6" md="3"><div class="field"><span class="field-label">Devol. Fim</span><span class="field-value">{{ formatDate(saldo.periodo_devol_fim) }}</span></div></b-col>
        </b-row>

        <!-- Valores -->
        <h6 class="saldo-view__section-title">
          <feather-icon icon="DollarSignIcon" size="14" class="mr-50" /> Valores
        </h6>
        <b-row class="saldo-view__grid">
          <b-col cols="6" md="4"><div class="field"><span class="field-label">Total Faturado</span><span class="field-value">{{ formatMoney(saldo.vl_total_fatur) }}</span></div></b-col>
          <b-col cols="6" md="4"><div class="field"><span class="field-label">Total Devolvido</span><span class="field-value">{{ formatMoney(saldo.vl_total_devol) }}</span></div></b-col>
          <b-col cols="6" md="4"><div class="field"><span class="field-label">Devol. Confirmado</span><span class="field-value">{{ formatMoney(saldo.vl_devol_confirmado) }}</span></div></b-col>
          <b-col cols="6" md="4"><div class="field"><span class="field-label">Devol. Pendente</span><span class="field-value">{{ formatMoney(saldo.vl_devol_pendente) }}</span></div></b-col>
          <b-col cols="6" md="4"><div class="field"><span class="field-label">Saldo Confirmado</span><span class="field-value">{{ formatMoney(saldo.vl_saldo_confirmado) }}</span></div></b-col>
          <b-col cols="6" md="4"><div class="field"><span class="field-label">Saldo Final</span><span class="field-value field-value--strong">{{ formatMoney(saldo.vl_saldo_final) }}</span></div></b-col>
        </b-row>
      </div>
    </div>
  </b-modal>
</template>

<script>
export default {
  name: 'ModalVisualizarSaldo',
  props: {
    saldo: {
      type: Object,
      default: null,
    },
  },
  methods: {
    formatDate(d) {
      if (!d) return '—';
      const dt = new Date(d);
      if (Number.isNaN(dt.getTime())) return '—';
      return dt.toLocaleDateString('pt-BR', { timeZone: 'UTC' });
    },
    formatMoney(v) {
      const n = Number(v);
      if (Number.isNaN(n)) return '—';
      return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
    },
    formatPerc(v) {
      const n = Number(v);
      if (Number.isNaN(n)) return '—';
      return `${n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;
    },
    formatTipoDevol(v) {
      const n = Number(v);
      if (n === 1) return 'DIDÁTICO';
      if (n === 2) return 'PARADIDÁTICO';
      return '—';
    },
  },
};
</script>

<style scoped>
.saldo-view__header {
  position: relative;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 20px 24px;
  background: linear-gradient(135deg, #ea5455 0%, #b83b3c 100%);
  color: #fff;
}
.saldo-view__title {
  font-size: 1.15rem;
  font-weight: 600;
  display: flex;
  align-items: center;
}
.saldo-view__subtitle {
  font-size: 0.85rem;
  opacity: 0.9;
  margin-top: 4px;
}
.saldo-view__badges {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  margin-right: 28px;
}
.saldo-view__close {
  position: absolute;
  top: 10px;
  right: 10px;
  color: #fff !important;
  padding: 4px;
}
.saldo-view__body {
  padding: 20px 24px 24px;
}
.saldo-view__highlight {
  display: flex;
  gap: 12px;
  margin-bottom: 20px;
}
.saldo-view__highlight-item {
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 14px 16px;
  border-radius: 8px;
  background: rgba(115, 103, 240, 0.08);
}
.saldo-view__highlight-item .label {
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  opacity: 0.7;
}
.saldo-view__highlight-item .value {
  font-size: 1.15rem;
  font-weight: 600;
  margin-top: 4px;
}
.saldo-view__highlight-item .value--primary {
  color: #7367f0;
  font-size: 1.35rem;
}
.saldo-view__section-title {
  font-weight: 600;
  margin: 18px 0 10px;
  display: flex;
  align-items: center;
  color: inherit;
  opacity: 0.85;
}
.saldo-view__grid {
  row-gap: 12px;
}
.field {
  display: flex;
  flex-direction: column;
}
.field-label {
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  opacity: 0.6;
}
.field-value {
  font-size: 0.95rem;
  font-weight: 500;
  margin-top: 2px;
}
.field-value--strong {
  font-weight: 700;
  color: #28c76f;
}
</style>
